'use client';

import {
  Carousel,
  CarouselContent,
  CarouselControls,
  CarouselItem,
} from '@/components';
import {
  ECOSYSTEM_PROJECTS,
  EcosystemCategory,
} from './EcosystemProjectsCarousel.data';
import { breakout, fadeOut } from '@/styles';
import { css, cx } from '@onbeam/business-styled-system/css';
import {
  flex,
  hstack,
  linkOverlay,
  text,
  vstack,
} from '@onbeam/business-styled-system/patterns';
import { Button, Link } from '@onbeam/business-ui';
import Image from 'next/image';
import { ArrowDiagonalIcon } from '@onbeam/icons/business';
import { useState } from 'react';
import { EcosystemProjectsCarouselProps } from './EcosystemProjectsCarousel.types';

export const EcosystemProjectsCarousel = ({
  className,
  withTabs = true,
}: EcosystemProjectsCarouselProps) => {
  const [activeTab, setActiveTab] = useState(EcosystemCategory.FEATURED);

  return (
    <div className={cx(breakout(), vstack({ gap: '10' }), className)}>
      {withTabs && (
        <div
          className={cx(
            fadeOut(),
            flex({
              sm: { w: 'full' },
              md: { mb: '6' },
              _before: {
                w: '[4rem]',
                sm: {
                  display: 'none',
                },
              },
              _after: {
                w: '[4rem]',
                sm: {
                  display: 'none',
                },
              },
            }),
          )}
        >
          <div
            className={flex({
              gap: '3',
              mx: 'auto',
              md: { gap: '4' },
              smDown: {
                overflowX: 'scroll',
                px: '6',
                py: '1',
              },
              scrollbarWidth: '[none]',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            })}
          >
            {Object.values(EcosystemCategory).map((tab) => (
              <Button
                key={tab}
                className={css({ flexShrink: '0' })}
                variant={activeTab === tab ? 'secondary' : 'ghost'}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </Button>
            ))}
          </div>
        </div>
      )}
      <Carousel className={vstack({ gap: '10' })}>
        <CarouselContent className={css({ pl: { base: '6', md: '8' } })}>
          {ECOSYSTEM_PROJECTS.filter((item) =>
            item.categories.includes(activeTab),
          ).map((item) => (
            <CarouselItem
              key={item.title}
              className={cx(
                'group',
                vstack({
                  position: 'relative',
                  gap: '6',
                  pr: '3.5',
                  md: { pr: '5' },
                  mb: '2',
                }),
              )}
            >
              <div
                className={css({
                  position: 'relative',
                  h: '[33.75rem]',
                  w: '[24.375rem]',
                  overflow: 'hidden',
                  rounded: 'xs',
                  maxW: '[calc(100dvw - token(spacing.12))]',
                  boxShadow:
                    '[0.2px 0.2px 4px 0px token(colors.mono.primaryBg/10)]',
                })}
              >
                <Image
                  src={item.image}
                  width={439}
                  height={180}
                  alt=""
                  className={css({
                    position: 'absolute',
                    zIndex: -1,
                    inset: '0',
                    h: 'full',
                    w: 'full',
                    objectFit: 'cover',
                    transitionProperty: 'transform',
                    transitionDuration: 'normal',
                    transitionTimingFunction: 'ease-out',
                    _groupHover: {
                      transform: 'translateY(-140px)',
                    },
                    mdDown: {
                      transform: 'translateY(-140px)',
                    },
                  })}
                />
                <div
                  className={vstack({
                    h: 'full',
                    gap: '2',
                    px: '5',
                    py: '6',
                    justify: 'flex-end',
                  })}
                >
                  <Link
                    href={item.href}
                    className={cx(
                      linkOverlay(),
                      text({
                        gap: '1',
                        color: 'mono.90',
                        style: 'subtitle-sm',
                        w: 'fit',
                      }),
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    iconSize={24}
                    iconVisibleOnMobile
                    iconAlwaysVisible
                  >
                    {item.title}
                  </Link>
                  <span
                    className={text({
                      style: 'body-sm',
                      color: 'mono.tertiary',
                    })}
                  >
                    {item.subtitle}
                  </span>
                </div>
                <div
                  className={vstack({
                    position: 'absolute',
                    left: '0',
                    right: '0',
                    bottom: '0',
                    gap: '2',
                    py: '6',
                    px: '5',
                    bg: 'mono.primaryFg',
                    pointerEvents: 'none',
                    h: '[19.5rem]',
                    transitionProperty: 'transform',
                    transitionDuration: 'normal',
                    transitionTimingFunction: 'ease-out',
                    transform: 'translateY(100%)',
                    _groupHover: {
                      transform: 'translateY(0%)',
                    },
                    mdDown: {
                      transform: 'translateY(0%)',
                    },
                    '@media(max-width: 24rem)': { h: '[22rem]' },
                    '@media(max-width: 26rem)': { h: '[20rem]' },
                  })}
                >
                  <h3
                    className={hstack({
                      textStyle: 'subtitle-sm',
                      align: 'center',
                      gap: '1',
                    })}
                  >
                    {item.title} <ArrowDiagonalIcon width={24} height={24} />
                  </h3>
                  <h4
                    className={text({
                      style: 'body-sm',
                      color: 'mono.tertiary',
                    })}
                  >
                    {item.subtitle}
                  </h4>
                  <p className={text({ style: 'body-sm', mr: '6' })}>
                    {item.description}
                  </p>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselControls className={css({ mx: 'auto' })} />
      </Carousel>
    </div>
  );
};
